"use client";

import React from 'react';
import { 
  ClipboardList,
  Users,
  Calendar,
  Code,
  Wrench,
  BrainCircuit,
  ClipboardCheck,
  RefreshCw,
  ArrowRight
} from 'lucide-react';
import { motion } from 'framer-motion';

interface ModelStep {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface EngagementModel {
  title: string;
  description: string;
  steps: ModelStep[];
  color: string;
  bestFor: string[];
}

const models: EngagementModel[] = [
  {
    title: "Fixed Scope Software Development Model",
    description: "Best for custom software projects with clearly defined requirements, fixed timelines, and predetermined deliverables—ideal for businesses seeking predictable costs and outcomes.",
    color: "from-indigo-500 to-blue-500",
    bestFor: [
      "Short-term software projects",
      "MVP development services",
      "Projects with fixed budgets",
      "Well-defined software requirements"
    ],
    steps: [
      {
        icon: <ClipboardList className="h-6 w-6" />,
        title: "Requirements Gathering and Analysis",
        description: "Detailed project scoping to define specifications, goals, and success criteria."
      },
      {
        icon: <Calendar className="h-6 w-6" />,
        title: "Kick-off and Strategic     Planning",
        description: "Roadmap creation, sprint planning, and software development resource allocation."
      },
      {
        icon: <Code className="h-6 w-6" />,
        title: "Agile Development and Milestones",
        description: "Custom software development with regular progress updates and quality assurance."
      },
      {
        icon: <Wrench className="h-6 w-6" />,
        title: "Final Delivery and Post-Launch Support",
        description: "Deployment of the finished solution with optional ongoing software maintenance."
      }
    ]
  },
  {
    title: "Extended Team Software Development Model",
    description: "Perfect for long-term software projects and dedicated development team services—ideal for businesses needing flexibility, scalability, and continuous delivery.",
    color: "from-purple-500 to-fuchsia-500",
    bestFor: [
      "Long-term software development",
      "Ongoing app and web development",
      "Teams needing flexible staffing",
      "Scalable development resources"
    ],
    steps: [
      {
        icon: <BrainCircuit className="h-6 w-6" />,
        title: "Initial Evaluation and Skill Matching",
        description: "Assessment of project goals and allocation of the right development expertise."
      },
      {
        icon: <ClipboardCheck className="h-6 w-6" />,
        title: "Task Assignment and Resource Allocation",
        description: "Assigning tasks to developers with specialized skills for faster delivery."
      },
      {
        icon: <Code className="h-6 w-6" />,
        title: "Continuous Development and Iteration",
        description: "Agile-based development with frequent releases and feedback loops."
      },
      {
        icon: <RefreshCw className="h-6 w-6" />,
        title: "Monthly Renewal and Scaling",
        description: "Flexible engagement allowing team size and skills to scale with your needs."
      }
    ]
  }
];


const EngagementModelsSection = () => {
  return (
    <section id="engagement-models" className="py-16 md:py-24 relative overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-20 left-10 w-60 h-60 rounded-full bg-gray-600 filter blur-[80px] animate-float"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-purple-600 filter blur-[100px] animate-float-delay"></div>
        <div className="absolute top-1/3 right-1/4 w-40 h-40 rounded-full bg-indigo-600 filter blur-[70px] animate-float"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 40 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6 }} 
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Flexible <span className="bg-gradient-to-r from-purple-400 to-white bg-clip-text text-transparent">Engagement Models</span>
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto">
            Choose the collaboration approach that best fits your project needs and business objectives.
          </p>
        </motion.div>

        <div className="flex flex-col gap-10 md:gap-16 max-w-6xl mx-auto">
          {models.map((model, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              className="group relative bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              {/* Gradient accent */}
              <div className={`absolute inset-x-0 top-0 h-2 bg-gradient-to-r ${model.color}`}></div>
              
              <div className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-start justify-between mb-6 md:mb-8">
                  <div className="mb-4 md:mb-0">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                      {model.title}
                    </h3>
                    <p className="text-gray-300 text-sm md:text-base">
                      {model.description}
                    </p>
                  </div>
                  <div className={`bg-gradient-to-br ${model.color} w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center text-white shadow-md`}>
                    <Users className="h-5 w-5 md:h-6 md:w-6" />
                  </div>
                </div>

                {/* Vertical Flowchart steps for mobile, horizontal for desktop */}
                <div className="relative mt-8 md:mt-10 mb-6 md:mb-8">
                  {/* Connecting line - hidden on mobile, visible on desktop */}
                
                  
                  <div className="relative grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4">
                    {model.steps.map((step : any, stepIndex) => (
                      <div key={stepIndex} className="relative z-10">
                        <div className="flex flex-col items-center">
                          <div className={`bg-gradient-to-br ${model.color} rounded-full p-2 md:p-3 mb-3 md:mb-4 shadow-lg`}>
                            {React.cloneElement(step.icon, { className: "h-5 w-5 md:h-6 md:w-6" })}
                          </div>
                          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-3 md:p-4 w-full text-center">
                            <h4 className="font-semibold text-white mb-1 text-sm md:text-base">{step.title}</h4>
                            <p className="text-gray-300 text-xs md:text-sm">{step.description}</p>
                          </div>
                        </div>
                        
                        {/* Vertical connector for mobile */}
                        {stepIndex < model.steps.length - 1 && (
                          <>
                            <div className="md:hidden absolute top-full left-1/2 w-0.5 h-4 bg-gradient-to-b from-gray-700/50 to-transparent transform -translate-x-1/2"></div>
                            <div className="hidden md:block absolute top-1/2 right-0 h-0.5 w-4 bg-gradient-to-r from-gray-700/50 to-transparent transform translate-x-full"></div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Best for section */}
                <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-700/50">
                  <h4 className="font-medium text-white mb-2 md:mb-3 text-sm md:text-base">Best for:</h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {model.bestFor.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center">
                        <div className={`w-1.5 h-1.5 rounded-full mr-2 bg-gradient-to-r ${model.color}`}></div>
                        <span className="text-xs md:text-sm text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-16 md:mt-20 bg-gradient-to-r from-gray-800/90 to-gray-700/90 backdrop-blur-md rounded-2xl p-6 md:p-10 lg:p-14 text-center text-white max-w-5xl mx-auto shadow-2xl relative overflow-hidden border border-white/10"
        >
          {/* Decorative pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500 rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-purple-500 rounded-full blur-2xl"></div>
          </div>
          
          <div className="relative z-10">
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-5">
              Not Sure Which Model <span className="text-purple-300">Fits Your Needs?</span>
            </h3>
            <p className="text-gray-300 text-base md:text-lg mb-6 md:mb-8 max-w-3xl mx-auto">
              Our experts will analyze your project requirements and recommend the optimal engagement model to ensure success.
            </p>
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center justify-center bg-white/90 text-gray-900 font-semibold py-3 px-6 md:px-8 rounded-lg shadow-md hover:bg-white transition duration-300 text-base md:text-lg"
            >
              Get a Personalized Recommendation
              <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
            </motion.a>
          </div>
        </motion.div>
      </div>

      {/* Floating animation styles */}
      <style jsx global>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-20px) translateX(10px);
          }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-delay {
          animation: float 10s ease-in-out infinite 2s;
        }
      `}</style>
    </section>
  );
};

export default EngagementModelsSection;